import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, MessageSquare } from 'lucide-react';
import { ReviewCard } from '../components/ReviewCard';
import { ReviewDialog } from '../components/ReviewDialog';
import { StarRating } from '../components/StarRating';
import { SEO } from '../components/SEO';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Reviews = () => {
  const { i18n } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [reviewsStats, setReviewsStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageContent, setPageContent] = useState(null);
  const lang = i18n.language;

  useEffect(() => {
    fetchReviews();
    fetchReviewsStats();
    fetchPageContent();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/approved`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewsStats = async () => {
    try {
      const response = await axios.get(`${API}/reviews/stats`);
      setReviewsStats(response.data);
    } catch (error) {
      console.error('Error fetching reviews stats:', error);
    }
  };

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/pages-content`);
      if (response.data && response.data.pages && response.data.pages.reviews) {
        setPageContent(response.data.pages.reviews);
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  // Хелпер для получения текста с фоллбэком
  const getText = (field, fallbackRu, fallbackLv) => {
    if (pageContent && pageContent[field]) {
      if (typeof pageContent[field] === 'object') {
        return pageContent[field][lang] || pageContent[field].ru || (lang === 'lv' ? fallbackLv : fallbackRu);
      }
      return pageContent[field];
    }
    return lang === 'lv' ? fallbackLv : fallbackRu;
  };

  // Текст рейтинга с подстановкой количества
  const getRatingText = () => {
    const template = getText('rating_text', 'На основе {count} отзывов', 'Balstīts uz {count} atsauksmēm');
    return template.replace('{count}', reviewsStats.total_reviews.toString());
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="reviews-page">
      <SEO 
        title={getText('page_title', 'Отзывы клиентов', 'Klientu atsauksmes')}
        description={lang === 'lv'
          ? 'Lasiet mūsu klientu atsauksmes par Pure Atelier Riga juvelierizstrādājumiem'
          : 'Читайте отзывы наших клиентов об украшениях Pure Atelier Riga'}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Star className="w-8 h-8 text-accent" fill="#C9A961" />
            <h1 className="font-heading text-5xl md:text-6xl tracking-tight" data-testid="reviews-title">
              {getText('page_title', 'Отзывы клиентов', 'Klientu atsauksmes')}
            </h1>
          </div>
          
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <StarRating rating={5} size={28} />
              <span className="font-heading text-4xl text-accent">{reviewsStats.average_rating}</span>
            </div>
            <span className="text-lg text-muted-foreground">
              {getRatingText()}
            </span>
          </div>

          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            {getText('page_description',
              'Наши клиенты делятся своим опытом с украшениями Pure Atelier',
              'Mūsu klienti dalās savā pieredzē ar Pure Atelier juvelierizstrādājumiem'
            )}
          </p>

          <Button 
            size="lg"
            onClick={() => setReviewOpen(true)}
            className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground uppercase tracking-widest transition-colors duration-500"
            data-testid="leave-review-button"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            {lang === 'lv' ? 'Atstāt atsauksmi' : 'Оставить отзыв'}
          </Button>
        </motion.div>

        {loading ? (
          <div className="text-center py-12">
            <p className="font-body text-muted-foreground">
              {lang === 'lv' ? 'Ielādē...' : 'Загрузка...'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <ReviewCard key={review.id} review={review} index={index} />
            ))}
          </div>
        )}
      </div>

      <ReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} />
    </div>
  );
};
